import { cva, type VariantProps } from "class-variance-authority";

/**
 * 🎨 NAVIGATION COMPOSITIONS - Carbon Copper Design System
 * Componente pentru navigation și menu systems
 */

/**
 * Navigation container
 */
export const navigation = cva([
  "flex items-center transition-[background-color,border-color] duration-200"
], {
  variants: {
    variant: {
      primary: [
        "bg-carbon-50 border-b border-carbon-200",
        "dark:bg-carbon-900 dark:border-carbon-700"
      ],
      secondary: [
        "bg-copper-50 border-b border-copper-200",
        "dark:bg-copper-900/20 dark:border-copper-700/50"
      ],
      transparent: "bg-transparent"
    },
    orientation: {
      horizontal: "flex-row",
      vertical: "flex-col"
    },
    size: {
      sm: "h-12 px-4",
      md: "h-16 px-6",
      lg: "h-20 px-8"
    }
  },
  defaultVariants: { variant: "primary", orientation: "horizontal", size: "md" }
});

/**
 * 🎯 SIDEBAR CVA - Styling complet pentru sidebar dintr-un singur loc
 * Gestionează toate aspectele: layout, spacing, responsive, states
 */
export const sidebar = cva([
  // Base styles - sempre aplicabile
  "flex flex-col transition-[transform,width] duration-300 ease-in-out",
  "bg-white dark:bg-carbon-900",
  "border-r border-carbon-200 dark:border-carbon-700"
], {
  variants: {
    variant: {
      desktop: [
        // Desktop: sticky sidebar cu height complet
        "hidden lg:flex h-screen sticky top-0 z-30",
        // 🔧 FIX: Prevent content overflow issues
        "overflow-hidden"
      ],
      mobile: [
        // Mobile: fixed overlay sidebar
        "fixed left-0 top-0 h-full z-50 lg:hidden",
        "shadow-2xl"
      ]
    },
    state: {
      expanded: "w-64",
      collapsed: "w-16", // 🔧 FIX: Redus de la w-18 la w-16 pentru a preveni text overflow
      // Mobile specific states
      visible: "translate-x-0",
      hidden: "-translate-x-full"
    },
    interactive: {
      true: "hover:shadow-lg",
      false: ""
    }
  },
  compoundVariants: [
    // Desktop + expanded
    {
      variant: "desktop",
      state: "expanded",
      class: "w-64"
    },
    // Desktop + collapsed  
    {
      variant: "desktop", 
      state: "collapsed",
      class: "w-16" // 🔧 FIX: Consistent cu state variant
    },
    // Mobile + visible
    {
      variant: "mobile",
      state: "visible", 
      class: "w-80 translate-x-0"
    },
    // Mobile + hidden
    {
      variant: "mobile",
      state: "hidden",
      class: "w-80 -translate-x-full"
    }
  ],
  defaultVariants: { 
    variant: "desktop", 
    state: "expanded",
    interactive: false
  }
});

/**
 * 🎯 SIDEBAR HEADER - Header cu logo și toggle button
 */
export const sidebarHeader = cva([
  "flex items-center border-b border-carbon-200 dark:border-carbon-700",
  "transition-[transform,opacity] duration-300"
], {
  variants: {
    state: {
      expanded: "justify-between p-4",
      collapsed: [
        "flex-col justify-start items-center py-4 px-2 gap-4", // 🔧 FIX: Mai mult spațiu vertical
        "min-h-[120px]" // 🔧 FIX: Height minim pentru a preveni cramping
      ],
      visible: "justify-between p-4", // Mobile visible = expanded layout
      hidden: "justify-between p-4"   // Mobile hidden = expanded layout (când e vizibil)
    }
  },
  defaultVariants: { state: "expanded" }
});

/**
 * 🎯 SIDEBAR CONTENT - Container pentru navigation items
 */
export const sidebarContent = cva([
  "flex-1 overflow-y-auto"
], {
  variants: {
    variant: {
      default: "p-4",
      compact: "p-2", 
      spacious: "p-6"
    },
    state: {
      expanded: "",
      collapsed: "px-2 py-4 space-y-2" // 🔧 FIX: Spațiu între items pentru collapsed
    }
  },
  defaultVariants: { variant: "default", state: "expanded" }
});

/**
 * 🎯 SIDEBAR ITEM - Item individual din sidebar cu toate states
 */
export const sidebarItem = cva([
  "flex items-center rounded-lg transition-[background-color,color] duration-200",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copper-500/20",
  // 🔧 FIX: Position relative pentru active indicator
  "relative overflow-hidden"
], {
  variants: {
    variant: {
      default: [
        "text-carbon-700 hover:text-copper-600 hover:bg-copper-50",
        "dark:text-carbon-300 dark:hover:text-copper-400 dark:hover:bg-copper-900/20"
      ],
      active: [
        "text-copper-600 font-medium",
        "dark:text-copper-400",
        // 🔧 FIX: Removed bg-copper-100 from here - will use indicator instead
      ],
      ghost: [
        "text-carbon-600 hover:bg-carbon-100",
        "dark:text-carbon-400 dark:hover:bg-carbon-800"
      ]
    },
    state: {
      expanded: "justify-start px-3 py-2 mx-2 mb-2",
      collapsed: [
        "justify-center mx-auto mb-2",  // 🔧 FIX: Mai mult spațiu între iconițe
        // 🔧 FIX: Container pătrat perfect pentru iconițe
        "w-12 h-12"
      ],
      transitioning: [
        // 🔧 FIX: State special pentru tranziție
        "justify-center mx-auto mb-2 w-12 h-12",
        "animate-pulse opacity-50" // Animație subtilă în timpul tranziției
      ]
    },
    size: {
      sm: "text-sm",
      md: "text-base", 
      lg: "text-lg"
    }
  },
  compoundVariants: [
    // 🔧 FIX: Active + expanded = background highlight
    {
      variant: "active",
      state: "expanded",
      class: "bg-copper-100 dark:bg-copper-900/30"
    },
    // 🔧 FIX: Active + collapsed = special highlight
    {
      variant: "active", 
      state: "collapsed",
      class: [
        "bg-copper-100 dark:bg-copper-900/30",
        "ring-2 ring-copper-500/20 dark:ring-copper-400/20"
      ]
    },
    // 🔧 FIX: Better hover for collapsed state
    {
      variant: "default",
      state: "collapsed",
      class: "hover:bg-carbon-100 dark:hover:bg-carbon-800"
    }
  ],
  defaultVariants: { 
    variant: "default", 
    state: "expanded",
    size: "md"
  }
});

/**
 * 🎯 SIDEBAR OVERLAY - Pentru mobile
 */
export const sidebarOverlay = cva([
  "fixed inset-0 bg-carbon-900/50 backdrop-blur-sm",
  "transition-opacity duration-300 lg:hidden z-40"
], {
  variants: {
    visible: {
      true: "opacity-100",
      false: "opacity-0 pointer-events-none"
    }
  },
  defaultVariants: { visible: false }
});

/**
 * Navigation item/link
 */
export const navigationItem = cva([
  "inline-flex items-center px-4 py-2 font-medium",
  "transition-[background-color,color,transform] duration-200 ease-in-out",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
], {
  variants: {
    variant: {
      default: [
        "text-carbon-700 hover:text-copper-600",
        "dark:text-carbon-300 dark:hover:text-copper-400"
      ],
      active: [
        "text-copper-600 bg-copper-50",
        "dark:text-copper-400 dark:bg-copper-900/20"
      ],
      ghost: [
        "text-carbon-600 hover:bg-carbon-100",
        "dark:text-carbon-400 dark:hover:bg-carbon-800"
      ]
    },
    size: {
      sm: "px-2 py-1 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg"
    }
  },
  defaultVariants: { variant: "default", size: "md" }
});

/**
 * Breadcrumb navigation
 */
export const breadcrumb = cva([
  "flex items-center space-x-2 text-sm",
  "text-carbon-600 dark:text-carbon-400"
], {
  variants: {
    variant: {
      default: "",
      compact: "space-x-1 text-xs"
    }
  },
  defaultVariants: { variant: "default" }
});

/**
 * Breadcrumb separator
 */
export const breadcrumbSeparator = cva([
  "text-carbon-400 dark:text-carbon-600"
], {
  variants: {
    type: {
      slash: "before:content-['/']",
      chevron: "before:content-['›']",
      arrow: "before:content-['→']"
    }
  },
  defaultVariants: { type: "chevron" }
});

/**
 * 🎯 SIDEBAR TOGGLE BUTTON - Button pentru expand/collapse
 */
export const sidebarToggleButton = cva([
  "rounded-lg transition-[background-color,color] duration-200",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copper-500/20"
], {
  variants: {
    state: {
      expanded: "p-1.5",
      collapsed: [
        "p-2",
        "bg-carbon-100 dark:bg-carbon-800", // 🔧 FIX: Background pentru vizibilitate
        "hover:bg-carbon-200 dark:hover:bg-carbon-700"
      ]
    },
    variant: {
      default: [
        "text-carbon-600 hover:text-copper-600 hover:bg-copper-50",
        "dark:text-carbon-400 dark:hover:text-copper-400 dark:hover:bg-copper-900/20"
      ],
      ghost: [
        "text-carbon-500 hover:bg-carbon-100",
        "dark:text-carbon-500 dark:hover:bg-carbon-800"
      ]
    }
  },
  defaultVariants: { state: "expanded", variant: "default" }
});

/**
 * 🎯 SIDEBAR LOGO CONTAINER - Container pentru logo
 */
export const sidebarLogoContainer = cva([
  "text-copper-600 dark:text-copper-400 flex-shrink-0"
], {
  variants: {
    state: {
      expanded: "w-8 h-8",
      collapsed: "w-10 h-10" // 🔧 FIX: Eliminat mb-2 pentru spacing mai bun
    }
  },
  defaultVariants: { state: "expanded" }
});

/**
 * 🎯 SIDEBAR TITLE - Titlul sidebar-ului
 */
export const sidebarTitle = cva([
  "truncate"
], {
  variants: {
    variant: {
      default: [
        "text-xl font-bold",
        "text-copper-600 dark:text-copper-400"
      ],
      subtitle: [
        "text-lg font-semibold",
        "text-carbon-900 dark:text-carbon-100"
      ]
    }
  },
  defaultVariants: { variant: "default" }
});

/**
 * 🎯 SIDEBAR ICON CONTAINER - Container pentru iconițe în items
 */
export const sidebarIconContainer = cva([
  "flex-shrink-0 flex items-center justify-center",
  "transition-[opacity,transform] duration-300 ease-in-out"
], {
  variants: {
    state: {
      expanded: "w-5 h-5 mr-3",
      collapsed: "w-5 h-5"
    }
  },
  defaultVariants: { state: "expanded" }
});

/**
 * 🎯 SIDEBAR ITEM TEXT - Text pentru sidebar items
 */
export const sidebarItemText = cva([
  "truncate flex-1 text-left"
], {
  variants: {
    variant: {
      default: "",
      bold: "font-medium"
    }
  },
  defaultVariants: { variant: "default" }
});

/**
 * 🎯 SIDEBAR ACTIVE INDICATOR - Indicator pentru active state
 */
export const sidebarActiveIndicator = cva([
  "absolute rounded-lg -z-10"
], {
  variants: {
    state: {
      expanded: "hidden", // Nu avem nevoie de indicator în expanded - folosim background
      collapsed: "hidden" // 🔧 FIX: Hidden deoarece folosim ring pentru active state
    }
  },
  defaultVariants: { state: "collapsed" }
});

/**
 * Type exports pentru TypeScript
 */
export type NavigationProps = VariantProps<typeof navigation>;
export type NavigationItemProps = VariantProps<typeof navigationItem>;
export type BreadcrumbProps = VariantProps<typeof breadcrumb>;
export type BreadcrumbSeparatorProps = VariantProps<typeof breadcrumbSeparator>;
export type SidebarProps = VariantProps<typeof sidebar>;
export type SidebarItemProps = VariantProps<typeof sidebarItem>;
export type SidebarHeaderProps = VariantProps<typeof sidebarHeader>;
export type SidebarContentProps = VariantProps<typeof sidebarContent>;
export type SidebarOverlayProps = VariantProps<typeof sidebarOverlay>;
export type SidebarToggleButtonProps = VariantProps<typeof sidebarToggleButton>;
export type SidebarLogoContainerProps = VariantProps<typeof sidebarLogoContainer>;
export type SidebarTitleProps = VariantProps<typeof sidebarTitle>;
export type SidebarIconContainerProps = VariantProps<typeof sidebarIconContainer>;
export type SidebarItemTextProps = VariantProps<typeof sidebarItemText>;
export type SidebarActiveIndicatorProps = VariantProps<typeof sidebarActiveIndicator>; 
